import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ERA_LABELS } from "@/lib/constants";
import { useDebounce } from "@/hooks/useDebounce";
import { SCHOLAR_ENABLED } from "@/lib/features";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, X, ScrollText, Globe, BookOpen, AlertCircle,
  Link2, GraduationCap, Sparkles, Heart, ArrowRight, BarChart3,
  Bookmark, Clock, Settings,
} from "lucide-react";

/* ============================================
   Search Index — All navigable content
   ============================================ */

interface SearchItem {
  id: string;
  title: string;
  subtitle?: string;
  type: string;
  icon: React.ElementType;
  to: string;
  keywords: string[];
}

const SEARCH_INDEX: SearchItem[] = [
  // Pages
  { id: "p-seerah", title: "Seerah Explorer", subtitle: "Life of Prophet Muhammad ﷺ", type: "page", icon: ScrollText, to: "/seerah", keywords: ["seerah", "prophet", "biography", "sirah", "life"] },
  { id: "p-timeline", title: "World Timeline", subtitle: "Parallel timeline of world & Islamic events", type: "page", icon: Globe, to: "/timeline", keywords: ["timeline", "world", "history", "parallel", "byzantine", "sassanid"] },
  { id: "p-quran", title: "Quran Companion", subtitle: "Explore surahs, ayahs, and themes", type: "page", icon: BookOpen, to: "/quran", keywords: ["quran", "surah", "ayah", "tafsir", "verse"] },
  { id: "p-clarifier", title: "Misconception Clarifier", subtitle: "Addressing misconceptions with sources", type: "page", icon: AlertCircle, to: "/clarifier", keywords: ["misconception", "myth", "clarify", "debunk"] },
  { id: "p-isnad", title: "Isnad Explorer", subtitle: "Chains of narration & narrator biographies", type: "page", icon: Link2, to: "/isnad", keywords: ["isnad", "chain", "narrator", "hadith", "bukhari"] },
  { id: "p-learning", title: "Learning Paths", subtitle: "Guided courses in Islamic studies", type: "page", icon: GraduationCap, to: "/learning", keywords: ["learning", "course", "path", "study", "lesson"] },
  { id: "p-scholar", title: "Ask a Scholar", subtitle: SCHOLAR_ENABLED ? "AI-powered Q&A with source citations" : "Coming soon", type: "page", icon: Sparkles, to: "/scholar", keywords: ["ask", "scholar", "question", "ai", "query"] },
  { id: "p-dhikr", title: "Dhikr Companion", subtitle: "Morning & evening adhkar with counters", type: "page", icon: Heart, to: "/dhikr", keywords: ["dhikr", "adhkar", "morning", "evening", "tasbeeh", "remembrance"] },
  { id: "p-analytics", title: "Analytics Dashboard", subtitle: "Platform stats and engagement metrics", type: "page", icon: BarChart3, to: "/analytics", keywords: ["analytics", "stats", "dashboard", "metrics", "views"] },
  { id: "p-bookmarks", title: "My Bookmarks", subtitle: "Saved content across all domains", type: "page", icon: Bookmark, to: "/bookmarks", keywords: ["bookmarks", "saved", "favorites"] },
  { id: "p-settings", title: "Settings", subtitle: "Theme, font size, and preferences", type: "page", icon: Settings, to: "/settings", keywords: ["settings", "preferences", "dark", "theme", "font"] },

];

/* ============================================
   Live content search across the API
   ============================================ */

function useContentSearch(query: string): { items: SearchItem[]; isFetching: boolean } {
  const q = useDebounce(query.trim(), 250);
  const enabled = q.length >= 2;
  const results = useQueries({
    queries: [
      { queryKey: ["search", "seerah", q], queryFn: () => api.seerah.events({ search: q, page_size: 5 }), enabled },
      { queryKey: ["search", "quran", q], queryFn: () => api.quran.surahs({ search: q }), enabled },
      { queryKey: ["search", "clarifier", q], queryFn: () => api.clarifier.misconceptions({ search: q, page_size: 5 }), enabled },
      { queryKey: ["search", "world", q], queryFn: () => api.worldHistory.events({ search: q, page_size: 5 }), enabled },
    ],
  });
  const [seerah, quran, clarifier, world] = results;

  const items: SearchItem[] = enabled
    ? [
        ...(seerah.data?.events ?? []).map((e) => ({
          id: `s-${e.id}`, title: e.title, type: "seerah", icon: ScrollText, to: `/seerah/${e.id}`, keywords: [],
          subtitle: [ERA_LABELS[e.era] ?? e.era, e.dateGregorian].filter(Boolean).join(" · "),
        })),
        ...(quran.data?.surahs ?? []).slice(0, 5).map((s) => ({
          id: `q-${s.number}`, title: `Surah ${s.nameTransliteration}`, type: "quran", icon: BookOpen,
          to: `/quran/${s.number}`, keywords: [], subtitle: `${s.meaning} · ${s.ayahCount} ayahs`,
        })),
        ...(clarifier.data?.misconceptions ?? []).map((m) => ({
          id: `c-${m.id}`, title: m.title, type: "clarifier", icon: AlertCircle, to: `/clarifier#${m.id}`,
          keywords: [], subtitle: m.category,
        })),
        ...(world.data?.events ?? []).map((e) => ({
          id: `w-${e.id}`, title: e.title, type: "world", icon: Globe, to: `/timeline/${e.id}`,
          keywords: [], subtitle: e.dateDisplay,
        })),
      ]
    : [];
  return { items, isFetching: results.some((r) => r.isFetching) };
}

/* ============================================
   Recent Searches (localStorage)
   ============================================ */

const RECENT_KEY = "dod:recent-searches";
const MAX_RECENT = 5;

function getRecentSearches(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}

function addRecentSearch(query: string) {
  try {
    const recent = getRecentSearches().filter((r) => r !== query);
    recent.unshift(query);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // Storage unavailable (private mode); recent searches are a convenience only.
  }
}

/* ============================================
   Search Overlay Component — Full Keyboard Nav
   ============================================ */

export default function SearchOverlay({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Re-read on every render while open: cheap, and picks up searches made since.
  const recentSearches = isOpen ? getRecentSearches() : [];

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const content = useContentSearch(isOpen ? query : "");

  // Pages match locally; content comes from the API.
  const results = useMemo(() => {
    if (!query.trim()) return SEARCH_INDEX.slice(0, 9);
    const q = query.toLowerCase();
    const pages = SEARCH_INDEX.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.subtitle?.toLowerCase().includes(q) ||
        item.keywords.some((k) => k.includes(q)),
    ).slice(0, 4);
    return [...pages, ...content.items];
  }, [query, content.items]);

  // Reset active index when results change
  useEffect(() => { setActiveIndex(0); }, [results]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-search-item]");
    items[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleSelect = useCallback((item: SearchItem) => {
    if (query.trim()) addRecentSearch(query.trim());
    navigate(item.to);
    onClose();
  }, [navigate, onClose, query]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (results[activeIndex]) handleSelect(results[activeIndex]);
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
    }
  }, [results, activeIndex, handleSelect, onClose]);

  const handleRecentClick = (term: string) => {
    setQuery(term);
    inputRef.current?.focus();
  };

  const typeLabels: Record<string, string> = {
    page: "Page",
    seerah: "Seerah",
    quran: "Quran",
    clarifier: "Clarifier",
    world: "Timeline",
  };

  const typeColors: Record<string, string> = {
    page: "text-[var(--color-gold)]",
    seerah: "text-emerald-600",
    quran: "text-purple-600",
    clarifier: "text-rose-600",
    world: "text-amber-600",
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-[var(--color-midnight)]/60 backdrop-blur-sm z-[100]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed top-[12vh] left-1/2 -translate-x-1/2 w-full max-w-xl z-[101]"
          >
            <div className="mx-4 bg-white rounded-2xl shadow-2xl border border-[var(--color-cream-dark)] overflow-hidden">
              {/* Search Input */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--color-cream-dark)]">
                <Search className="w-5 h-5 text-[var(--color-gold)] shrink-0" />
                <input
                  ref={inputRef}
                  type="search"
                  aria-label="Search the site"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search seerah, quran, topics..."
                  className="flex-1 text-base outline-none placeholder:text-[var(--color-ink-muted)]/50"
                />
                <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--color-cream)] transition-colors" aria-label="Close search">
                  <X className="w-4 h-4 text-[var(--color-ink-muted)]" />
                </button>
              </div>

              {/* Recent Searches */}
              {!query.trim() && recentSearches.length > 0 && (
                <div className="px-5 pt-3 pb-1 border-b border-[var(--color-cream-dark)]">
                  <p className="text-[10px] text-[var(--color-ink-muted)]/60 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Recent
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {recentSearches.map((term) => (
                      <button
                        key={term}
                        onClick={() => handleRecentClick(term)}
                        className="text-xs px-2.5 py-1 rounded-full bg-[var(--color-cream)] text-[var(--color-ink-muted)] hover:bg-[var(--color-gold)]/10 hover:text-[var(--color-gold-dim)] transition-colors"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Results */}
              <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1">
                {results.length > 0 ? (
                  results.map((item, i) => (
                    <button
                      key={item.id}
                      data-search-item
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={`w-full flex items-center gap-3 px-5 py-3 transition-colors text-left ${
                        i === activeIndex
                          ? "bg-[var(--color-gold)]/8"
                          : "hover:bg-[var(--color-cream)]/50"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        i === activeIndex ? "bg-[var(--color-gold)]/15" : "bg-[var(--color-cream)]"
                      }`}>
                        <item.icon className={`w-4 h-4 ${i === activeIndex ? "text-[var(--color-gold)]" : "text-[var(--color-ink-muted)]"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          i === activeIndex ? "text-[var(--color-midnight)]" : "text-[var(--color-ink)]"
                        }`}>
                          {item.title}
                        </p>
                        {item.subtitle && (
                          <p className="text-xs text-[var(--color-ink-muted)] truncate">{item.subtitle}</p>
                        )}
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider shrink-0 font-medium ${
                        typeColors[item.type] || "text-[var(--color-ink-muted)]/60"
                      }`}>
                        {typeLabels[item.type] || item.type}
                      </span>
                      {i === activeIndex && <ArrowRight className="w-3.5 h-3.5 text-[var(--color-gold)] shrink-0" />}
                    </button>
                  ))
                ) : content.isFetching ? (
                  <p className="px-5 py-8 text-center text-sm text-[var(--color-ink-muted)]">Searching…</p>
                ) : (
                  <div className="px-5 py-8 text-center">
                    <Search className="w-8 h-8 text-[var(--color-cream-dark)] mx-auto mb-2" />
                    <p className="text-sm text-[var(--color-ink-muted)]">No results for "{query}"</p>
                    <p className="text-xs text-[var(--color-ink-muted)]/50 mt-1">Try a different keyword</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-2.5 border-t border-[var(--color-cream-dark)] flex items-center justify-between text-[10px] text-[var(--color-ink-muted)]/50">
                <span>{results.length} result{results.length !== 1 ? "s" : ""}</span>
                <div className="flex items-center gap-2">
                  <span>
                    <kbd className="px-1.5 py-0.5 rounded bg-[var(--color-cream)] text-[var(--color-ink-muted)] font-mono">↑↓</kbd> navigate
                  </span>
                  <span>
                    <kbd className="px-1.5 py-0.5 rounded bg-[var(--color-cream)] text-[var(--color-ink-muted)] font-mono">↵</kbd> select
                  </span>
                  <span>
                    <kbd className="px-1.5 py-0.5 rounded bg-[var(--color-cream)] text-[var(--color-ink-muted)] font-mono">esc</kbd> close
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
